
import type { TimelySearchData } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-data'
import type { TimelySearchResponse } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-response'
import type { PropType } from 'vue'
import type { Dictionary, PeriodsData } from '../../../models'
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import { useKpis } from '../../../composables/use-kpis'

import { TYPES } from '../../../config/inversify.types'

import { KpiEnum } from '../../../enums'

import { capitalizeSentence } from '../../../filters'

import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'

import { CONFIG_CONSTS } from '../../../store/config/config.consts'
import { UiTooltip } from '../../keen-ui'
import { EpbText } from '../../shared/epb-text'

export enum AnimationStepsIndexEnum {
  INITIAL_DELAY = 0,
  KPIS = 1,
}

export default defineComponent({
  name: 'SteppedKpisBar',
  components: {
    EpbText,
    UiTooltip,
  },
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    selectedId: String,
    animationStepDurationsInMillis: Array as PropType<Array<number>>,
    currentAnimationStep: Number,
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data,
    }),
    steppedKpisBarData(): Dictionary<number> {
      let distData: PeriodsData<TimelySearchResponse>
      if (!this.selectedId) {
        distData = this.data.overall.dist
      } else {
        const selectedIdData = this.data.ids[this.selectedId]
        if (selectedIdData) {
          distData = selectedIdData.dist
        }
      }
      const availableKpisInTemplate = [].concat(...this.kpisTemplateGroups)
      const data = availableKpisInTemplate.reduce((processedData, kpi: KpiEnum) => {
        processedData[kpi] = 0
        return processedData
      }, {})

      if (!!distData && !!distData.currentPeriod) {
        availableKpisInTemplate.forEach(
          (kpi: KpiEnum) => (data[kpi] = this.getKpiData(kpi, distData.currentPeriod.data)),
        )
      }
      return data
    },
    kpisTemplateGroups() {
      /** These are the two KPIs not shown in this graph: */
      const notDisplayedKpis = [KpiEnum.QUERIES, KpiEnum.CHECKOUT]

      /** We grab the supported KPIs from the provider and filter them, removing
        the previous two we do not want to show. **/
      const supportedKpis = useKpis().supportedKpis.filter(kpi => {
        return !notDisplayedKpis.includes(kpi)
      })

      /** We get the Findability Engagement KPI for future use */
      const findEngKpi = useKpis().findabilityEngagementKpi
      /** Now we create a structure usable for the component: an array of arrays.
       *  Each item in the array denotes the grouping of kpis to display:
       *  - If there is only one kpi, it will be displayed alone: [no results].
       *  - If there's two kpis, they will be displayed one on top of the
       *    other: [clicks, findability].
       *     - We group findabilityEngagementKpi (usually clicks) and findability.
       *     - Last, we remove empty/undefined slots
       */
      const initialKpiGroups = supportedKpis
        .map((kpi, index) => {
          if (kpi === findEngKpi && supportedKpis[index + 1] === KpiEnum.FINDABILITY) {
            return [findEngKpi, KpiEnum.FINDABILITY]
          } else if (kpi !== KpiEnum.FINDABILITY) {
            return [kpi]
          }
          return undefined
        })
        .filter(kpi => kpi !== undefined)

      /** We always add noFindability (usually noClicks) to the chart */
      const initialTemplateKpisGroups = [[this.noFindability], ...initialKpiGroups]

      return initialTemplateKpisGroups.reduce(
        (kpisTemplateGroups: any[], groupedKpis: string[]) => {
          const availableKpisInGroup: string[] = groupedKpis.filter(
            kpi => !(Object.values(KpiEnum) as string[]).includes(kpi) || this.kpis.includes(kpi),
          )
          if (availableKpisInGroup.length) {
            kpisTemplateGroups.push(availableKpisInGroup)
          }
          return kpisTemplateGroups
        },
        [],
      )
    },
    kpisDisplayGroups() {
      return [
        [this.noFindability, KpiEnum.CLICKS],
        [KpiEnum.FINDABILITY],
        [KpiEnum.ADD2CART, KpiEnum.CHECKOUT],
        [KpiEnum.NO_RESULTS],
      ].filter(group => group.some(kpi => this.kpis.includes(kpi)))
    },
    kpiTransitionDurationInMillis(): number {
      return (
        this.animationStepDurationsInMillis[AnimationStepsIndexEnum.KPIS] /
        this.kpisDisplayGroups.length
      )
    },
    kpisGroupsTotalPercentage(): number {
      return this.kpisTemplateGroups.reduce((totalPercentage: number, kpisGroup: string[]) => {
        totalPercentage += this.getKpisGroupMaxPercentage(kpisGroup)
        return totalPercentage
      }, 0)
    },
  },
  data() {
    return {
      noFindability: 'no-findability',
      lastKpisIndexDisplayed: undefined,
      resizeObserver: undefined,
      messages: this.$epb.messages.insights.analytics,
      AnimationStepsIndexEnum,
    }
  },
  watch: {
    currentAnimationStep(): void {
      if (this.currentAnimationStep === AnimationStepsIndexEnum.KPIS) {
        this.lastKpisIndexDisplayed = 0
      }
    },
  },
  mounted(): void {
    const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const { width } = entry.contentRect
        const entryTitles: Element[] = Array.from(
          entry.target.getElementsByClassName('epb-stepped-kpis-bar__text--kpi-label'),
        )
        entryTitles.forEach(
          (title: HTMLElement) =>
            (title.style.visibility =
              title.getBoundingClientRect().width > width ? 'hidden' : 'visible'),
        )
      })
    })
    this.$refs.kpisGroup.forEach(kpisGroup => this.resizeObserver.observe(kpisGroup))
  },
  beforeDestroy(): void {
    this.resizeObserver.disconnect()
  },
  methods: {
    getKpiData(kpi: string, dataset: TimelySearchData): number {
      return {
        [KpiEnum.CLICKS]: () => dataset.clickCountPct,
        [this.noFindability]: () => Math.max(0, dataset.noFindabilityPct || 0),
        [KpiEnum.FINDABILITY]: () => dataset.findability,
        [KpiEnum.ADD2CART]: () => dataset.add2cartCountPct,
        [KpiEnum.CHECKOUT]: () => dataset.checkoutCountPct,
        [KpiEnum.NO_RESULTS]: () => dataset.noResultsCountPct,
      }[kpi]()
    },
    getKpiLabel(kpi: string): string {
      const kpisMessages = this.messages.kpis
      const kpiMessage = {
        [KpiEnum.CLICKS]: () => kpisMessages[KpiEnum.CLICKS].percentageLabel,
        [this.noFindability]: () => this.messages.noFindability,
        [KpiEnum.FINDABILITY]: () => kpisMessages[KpiEnum.FINDABILITY].percentageLabel,
        [KpiEnum.ADD2CART]: () => kpisMessages[KpiEnum.ADD2CART].percentageLabel,
        [KpiEnum.CHECKOUT]: () => kpisMessages[KpiEnum.CHECKOUT].percentageLabel,
        [KpiEnum.NO_RESULTS]: () => kpisMessages.noResults.valueLabel,
      }[kpi]()

      return capitalizeSentence(kpiMessage)
    },
    getKpisGroupMaxPercentage(kpisGroup: string[]): number {
      return Math.max(...kpisGroup.map(kpi => this.steppedKpisBarData[kpi]))
    },
    getKpisDisplayGroupIndex(kpi: string): number {
      return this.kpisDisplayGroups.findIndex(kpisDisplayGroup => kpisDisplayGroup.includes(kpi))
    },
    getKpiTooltipText(kpi: string): string {
      return `${this.getKpiLabel(kpi)}: ${this.$epb.utils.formatNumber(
        this.steppedKpisBarData[kpi],
      )}%`
    },
    isKpiDisplayed(kpi: string): boolean {
      return this.getKpisDisplayGroupIndex(kpi) <= this.lastKpisIndexDisplayed
    },
  },
})
